/**
 * GradientField - ReactFlow custom node type for gradient fields.
 *
 * Renders as a resizable div with a linear gradient background.
 * Uses ReactFlow's built-in drag handling. Resize is handled via
 * corner/edge handles with the nodrag class to prevent drag interference.
 */

import React, { useState, useCallback, useRef } from 'react';
import type { NodeProps } from '@xyflow/react';

export interface GradientFieldData {
  fieldType: 'gradient';
  width: number;
  height: number;
  axis: 'x' | 'y';
  label?: string;
  color?: string;
  onResize?: (id: string, width: number, height: number) => void;
}

const MIN_SIZE = 40;
const HANDLE_SIZE = 10;

export function GradientField({ id, data }: NodeProps) {
  const { width: initWidth, height: initHeight, axis, label, color = '#3b82f6', onResize } = data as unknown as GradientFieldData;

  const [width, setWidth] = useState(initWidth);
  const [height, setHeight] = useState(initHeight);
  const resizeRef = useRef<{ startX: number; startY: number; origW: number; origH: number } | null>(null);

  const onResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    resizeRef.current = { startX: e.clientX, startY: e.clientY, origW: width, origH: height };

    const onMouseMove = (me: MouseEvent) => {
      if (!resizeRef.current) return;
      const dx = me.clientX - resizeRef.current.startX;
      const dy = me.clientY - resizeRef.current.startY;
      const newW = Math.max(MIN_SIZE, resizeRef.current.origW + dx);
      const newH = Math.max(MIN_SIZE, resizeRef.current.origH + dy);
      setWidth(newW);
      setHeight(newH);
      onResize?.(id, newW, newH);
    };

    const onMouseUp = () => {
      resizeRef.current = null;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [id, width, height, onResize]);

  const gradientDir = axis === 'y' ? 'to bottom' : 'to right';

  return (
    <div
      style={{
        width,
        height,
        background: `linear-gradient(${gradientDir}, ${color}0d, ${color}40)`,
        border: `1px solid ${color}66`,
        borderRadius: 4,
        position: 'relative'
      }}
    >
      {/* Label */}
      {label && (
        <div style={{
          position: 'absolute',
          top: 4,
          left: 6,
          color,
          fontSize: 11,
          opacity: 0.7,
          userSelect: 'none',
          pointerEvents: 'none'
        }}>
          {label}
        </div>
      )}

      {/* Resize handle (bottom-right corner) */}
      <div
        className="nodrag nopan"
        onMouseDown={onResizeMouseDown}
        style={{
          position: 'absolute',
          bottom: 0,
          right: 0,
          width: HANDLE_SIZE,
          height: HANDLE_SIZE,
          cursor: 'nwse-resize',
          background: `${color}4d`,
          borderRadius: '2px 0 4px 0'
        }}
      />
    </div>
  );
}
